import _styled, { default as _styled2 } from "styled-components";
const _excluded = ["style", "className", "id", "name", "checked", "disabled", "defaultChecked", "onChange", "readOnly", "inputProps", "data-testid"],
  _excluded2 = ["style"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], -1 === t.indexOf(o) && {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (-1 !== e.indexOf(n)) continue; t[n] = r[n]; } return t; }
import React from 'react';
import PropTypes from 'prop-types';
import { useControlled } from '@adsk/alloy-react-helpers';
import theme, { getInteractiveStyles, stylePropType } from '@adsk/alloy-react-theme';
/**
 * A Toggle
 * Provides a control to turn something on or off.
 * Toggling the switch should enable or select the active state.
 * (Don't phrase toggles with a negative and require clicking to turn something off.)
 * The rest of the props are propogated to the input element.
 */
const Toggle = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
      style,
      className,
      id,
      name,
      checked,
      disabled = false,
      defaultChecked = false,
      onChange: onChangeProp,
      readOnly,
      inputProps = {},
      'data-testid': dataTestid
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  const [value, handleOnChange] = useControlled(defaultChecked, checked, onChangeProp);
  const onChange = readOnly ? undefined : e => {
    !disabled && handleOnChange(!value, e);
  };
  const {
      style: inputStyle
    } = inputProps,
    rest = _objectWithoutProperties(inputProps, _excluded2);
  return /*#__PURE__*/React.createElement(_StyledDiv, _extends({
    className: className
  }, props, {
    $_css: [_objectSpread(_objectSpread(_objectSpread(_objectSpread({
      border: '1px solid',
      borderColor: theme.tokens.colors.border.value,
      borderRadius: theme.borderRadius.pill,
      cursor: 'pointer',
      height: 20,
      position: 'relative',
      width: 37
    }, value && _objectSpread({
      backgroundColor: theme.tokens.colors.background.selected.value,
      border: 'none'
    }, disabled && {
      backgroundColor: theme.tokens.colors.background.selected.inactive.value
    })), disabled && {
      borderColor: theme.tokens.colors.border.inactive.value
    }), getInteractiveStyles(disabled)), {}, {
      '&:after': _objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({
        backgroundColor: theme.tokens.colors.background.inverse.subtler.value
      }, disabled && {
        backgroundColor: theme.tokens.colors.background.neutral.inactive.value
      }), value && {
        backgroundColor: theme.tokens.colors.background.primary.value
      }), {}, {
        borderRadius: theme.borderRadius.circle,
        content: '""',
        display: 'block',
        height: 12
      }, !value && {
        left: 4
      }), {}, {
        // left: !value && 4,
        position: 'absolute'
      }, value && {
        right: 4
      }), {}, {
        // right: value && 4,
        top: value ? 4 : 3,
        width: 12
      })
    }), style]
  }), /*#__PURE__*/React.createElement(_StyledInput, _extends({
    ref: ref,
    id: id,
    name: name,
    role: "switch",
    "aria-label": "toggle",
    "aria-checked": value
    // value={value}
    ,
    onChange: onChange,
    type: "checkbox",
    checked: value,
    readOnly: readOnly,
    disabled: disabled,
    "data-testid": dataTestid
  }, rest, {
    $_css2: [{
      cursor: 'inherit',
      position: 'absolute',
      opacity: 0,
      width: '100%',
      height: '100%',
      top: 0,
      left: 0,
      margin: 0,
      padding: 0,
      zIndex: 1
    }, inputStyle]
  })));
});
Toggle.displayName = 'Toggle';
Toggle.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** The id of the checkbox, used for the label */
  id: PropTypes.string,
  /** The name for the checkbox input */
  name: PropTypes.string,
  /** The value of the toggle */
  checked: PropTypes.bool,
  /** The initial value of the toggle, this will be read only if `checked` is undefined */
  defaultChecked: PropTypes.bool,
  /** onChange handler for the checkbox input */
  onChange: PropTypes.func,
  /** Disables propagation stop */
  readOnly: PropTypes.bool,
  /** Disable checkbox functionallity */
  disabled: PropTypes.bool,
  /** Props passed to the input element */
  inputProps: PropTypes.object,
  /** data-testid passed to the input element */
  'data-testid': PropTypes.string
};
export default Toggle;
var _StyledDiv = _styled("div")`${p => p.$_css}`;
var _StyledInput = _styled2("input").withConfig({
  displayName: "Toggle___StyledInput",
  componentId: "sc-crezff-0"
})(["", ""], p => p.$_css2);
//# sourceMappingURL=Toggle.js.map